import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

const ToastContext = createContext();

export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        // Graceful fallback if used outside provider
        return { showToast: (msg) => console.warn('[Toast] No provider:', msg) };
    }
    return context;
};

const TOAST_ICONS = {
    success: '✓',
    error: '✕',
    info: 'ℹ',
};

const TOAST_COLORS = {
    success: { bg: '#ecfdf5', border: '#6ee7b7', text: '#065f46', icon: '#059669' },
    error: { bg: '#fef2f2', border: '#fca5a5', text: '#991b1b', icon: '#dc2626' },
    info: { bg: '#eff6ff', border: '#93c5fd', text: '#1e40af', icon: '#3b82f6' },
};

const Toast = ({ id, message, type = 'info', onDismiss }) => {
    const colors = TOAST_COLORS[type] || TOAST_COLORS.info;
    const icon = TOAST_ICONS[type] || TOAST_ICONS.info;
    const [exiting, setExiting] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => {
            setExiting(true);
            setTimeout(() => onDismiss(id), 300);
        }, 3000);
        return () => clearTimeout(timer);
    }, [id, onDismiss]);

    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '12px 16px',
                borderRadius: '12px',
                backgroundColor: colors.bg,
                border: `1px solid ${colors.border}`,
                color: colors.text,
                fontSize: '14px',
                fontWeight: 500,
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                minWidth: '280px',
                maxWidth: '400px',
                animation: exiting
                    ? 'toast-slide-out 0.3s ease-in forwards'
                    : 'toast-slide-in 0.3s ease-out forwards',
                cursor: 'pointer',
                userSelect: 'none',
            }}
            onClick={() => {
                setExiting(true);
                setTimeout(() => onDismiss(id), 300);
            }}
        >
            <span
                style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    backgroundColor: colors.icon,
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    flexShrink: 0,
                }}
            >
                {icon}
            </span>
            <span style={{ flex: 1, lineHeight: '1.4' }}>{message}</span>
        </div>
    );
};

export const ToastProvider = ({ children }) => {
    const [toasts, setToasts] = useState([]);
    const counterRef = useRef(0);

    const dismiss = useCallback((id) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    const showToast = useCallback((message, type = 'info') => {
        const id = ++counterRef.current;
        setToasts(prev => [...prev.slice(-4), { id, message, type }]); // Keep max 5
    }, []);

    return (
        <ToastContext.Provider value={{ showToast }}>
            {children}

            {/* Toast Container */}
            {toasts.length > 0 && (
                <div
                    style={{
                        position: 'fixed',
                        bottom: '24px',
                        right: '24px',
                        zIndex: 99999,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        pointerEvents: 'auto',
                    }}
                >
                    {toasts.map(t => (
                        <Toast key={t.id} id={t.id} message={t.message} type={t.type} onDismiss={dismiss} />
                    ))}
                </div>
            )}

            {/* Keyframe Animations */}
            <style>{`
                @keyframes toast-slide-in {
                    from { opacity: 0; transform: translateX(100%) scale(0.95); }
                    to { opacity: 1; transform: translateX(0) scale(1); }
                }
                @keyframes toast-slide-out {
                    from { opacity: 1; transform: translateX(0) scale(1); }
                    to { opacity: 0; transform: translateX(100%) scale(0.95); }
                }
            `}</style>
        </ToastContext.Provider>
    );
};
